import styled from '@emotion/styled';
import {
  createSvgIcon,
  Checkbox as MuiCheckbox,
  type SxProps,
} from '@mui/material';
import { Box } from '@ovComponents/1-primative/box/box';
import type { TypographyVariantOptions } from '@ovComponents/1-primative/typography/typography';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { type ComponentProps, useEffect, useState } from 'react';
import { HelpText } from '../helpText/helpText';
import { useHelpTextTokens } from '../helpText/helpText.tokens';
import InfoDialog from '../infoDialog/infoDialog';
import { useCheckboxTokens } from './checkbox.tokens';

type CheckboxFunctionalProps = {
  checked?: boolean;
  indeterminate?: boolean;
  onChange?: (onoff: boolean) => void;
  onClick?: (e: any) => void;
  disabled?: boolean;
  infoTooltip?: string;
  testId?: string;
};
type CheckboxVisualProps = {
  error?: boolean;
  label?: string;
  size?: 'medium' | 'small';
  chip?: boolean;
  customStyle?: SxProps;
  checkBoxStyle?: SxProps;
  labelVariant?: TypographyVariantOptions;
  centerInfoToolTip?: boolean;
};

export const Checkbox = ({
  checked = false,
  indeterminate = false,
  onChange,
  disabled = false,
  label,
  size = 'medium',
  chip = false,
  customStyle,
  checkBoxStyle,
  infoTooltip,
  labelVariant,
  centerInfoToolTip,
  onClick,
  testId,
  error,
}: CheckboxFunctionalProps & CheckboxVisualProps) => {
  const tokens = useThemeTokens(useCheckboxTokens());
  const { comp: helpTextComp } = useThemeTokens(useHelpTextTokens());
  const { comp, sys } = tokens;
  const [on, setOn] = useState<boolean>(checked);
  const variant =
    labelVariant || (size === 'small' ? 'bodySmall' : 'bodyLarge');

  const shouldDisplayInDialog = helpTextComp.helpText.displayInDialog;

  useEffect(() => {
    if (checked !== undefined) setOn(checked);
  }, [checked]);

  const setOnAndPropagate = (newOn: boolean) => {
    setOn(newOn);
    if (onChange) onChange(newOn);
  };

  return (
    <Box display='flex' flexDirection='column' sx={{ ...customStyle }}>
      <Box
        display='flex'
        flexDirection='row'
        sx={{
          background: chip
            ? checked
              ? sys.color.supportOneVariant
              : sys.color.surfaceContainerVariant
            : 'transparent',
          padding: chip ? '4px 8px' : undefined,
          width: chip ? 'fit-content' : undefined,
          borderRadius: sys.borderRadius.sm,
          mb: chip ? 0.5 : undefined,
          mr: chip ? 0.5 : undefined,
        }}
      >
        <StyledMuiCheckbox
          data-testid={testId ?? 'checkbox'}
          checked={on}
          indeterminate={indeterminate}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setOnAndPropagate(event.target.checked);
          }}
          size={size}
          disabled={disabled}
          checkedIcon={<CheckBoxIcon />}
          indeterminateIcon={<IndeterminateCheckBoxIcon />}
          onClick={onClick}
          error={error}
          sx={{
            padding: 0,
            ...checkBoxStyle,
          }}
          inputProps={
            {
              'data-testid': `${testId ?? 'checkbox'}-input`,
            } as React.InputHTMLAttributes<HTMLInputElement>
          }
        />
        {label !== undefined && (
          <div
            onClick={() => {
              if (!disabled) {
                setOnAndPropagate(!on);
              }
            }}
          >
            <Typography
              variant={variant}
              sx={{
                color: !disabled
                  ? error
                    ? sys.color.negative
                    : comp.checkbox.defaultColor
                  : comp.checkbox.disabledColor,
                cursor: !disabled ? 'pointer' : 'default',
                ml: 0.5,
              }}
            >
              {label}
            </Typography>
          </div>
        )}
        {infoTooltip && shouldDisplayInDialog && (
          <InfoDialog information={infoTooltip} center={centerInfoToolTip} />
        )}
      </Box>
      {infoTooltip && !shouldDisplayInDialog && (
        <HelpText text={infoTooltip} tone='neutral' />
      )}
    </Box>
  );
};

const StyledMuiCheckbox = styled(
  (props: ComponentProps<typeof MuiCheckbox> & { error?: boolean }) => (
    <MuiCheckbox {...props} />
  ),
)((props) => {
  const tokens = useThemeTokens(useCheckboxTokens());
  const { comp, sys } = tokens;

  return {
    '& svg.MuiSvgIcon-root': {
      color: props.error ? sys.color.negative : comp.checkbox.defaultColor,
      fontSize: props.size === 'small' ? 12 : 16,
    },
    '&.Mui-checked, &.MuiCheckbox-indeterminate': {
      '& svg.MuiSvgIcon-root': {
        color: comp.checkbox.activeColor,
      },
    },
    '&.Mui-disabled svg.MuiSvgIcon-root': {
      color: comp.checkbox.disabledColor,
    },
  };
});

/* Like CheckBoxIcon from @mui/icons-material, with rounded corners */
const CheckBoxIcon = createSvgIcon(
  <path d='M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V5c0-1.1-.89-2-2-2zm-10 13-3.5-3.5a1 1 0 011.41-1.41L10 14.17l6.56-6.67a1 1 0 011.44 1.5l-7 7a1.5 1.5 0 01-2 0' />,
  'CheckBoxIcon',
);

/* Like IndeterminateCheckBoxIcon from @mui/icons-material, with rounded corners */
const IndeterminateCheckBoxIcon = createSvgIcon(
  <path d='M19 3h-14c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-14c0-1.1-.9-2-2-2Zm-2 10h-10c-1 0-1-1-1-1 0-1 1-1 1-1h10c1 0 1 1 1 1 0 1-1 1-1 1z' />,
  'IndeterminateCheckBoxIcon',
);
